import { aspectValue, MAX_OPLOG_BYTES, opLogBytes, type Assignment } from '@panels/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DrawCanvas, type DrawCanvasHandle } from '../components/DrawCanvas.js';
import { PanelCanvas } from '../components/PanelCanvas.js';
import { ToolPicker } from '../components/ToolPicker.js';
import { Screen, Spinner } from '../components/ui.js';
import { activityReport, clearActivity, noteAction, noteInput, trackPanel } from '../lib/activity.js';
import { clearDraft, flushSubmission, loadDraft, queueSubmission, saveDraft, type SubmitState } from '../lib/drafts.js';
import { DrawingSession, pointsRewoundAfter, type SizeLevel } from '../lib/drawing.js';
import { TOOLS, type ToolId } from '../lib/tools.js';
import { useRoomState } from '../lib/useRoomState.js';

/** How long a press has to last before it counts as a hold rather than a tap. */
const HOLD_THRESHOLD_MS = 200;
const DRAFT_THROTTLE_MS = 1200;

/** Shown once per device, the first time a stroke straightens (Round 2 §2.5). */
const SNAP_HINT_KEY = 'panels:hint:snap';
const SNAP_HINT_MS = 3500;

type DrawLayout = 'focus' | 'split';
const LAYOUT_KEY = 'panels:drawLayout';

/**
 * The brief's split view (§7) keeps the previous panel beside the canvas, which
 * on a phone costs half the screen. Focus mode gives the canvas everything and
 * keeps the previous panel one glance away on the peek tab. Phones default to
 * focus; wide screens have room to share and default to split. Either choice is
 * remembered.
 */
function initialLayout(): DrawLayout {
  try {
    const stored = localStorage.getItem(LAYOUT_KEY);
    if (stored === 'focus' || stored === 'split') return stored;
  } catch {
    /* ignore */
  }
  return window.innerWidth < 700 ? 'focus' : 'split';
}

export function Draw() {
  const { roomId, panelId } = useParams<{ roomId: string; panelId: string }>();
  const navigate = useNavigate();
  // Every poll from this screen says how busy we are (Round 2 §1.1).
  const { state, loading } = useRoomState(roomId, { activity: activityReport });

  const session = useMemo(() => new DrawingSession(), []);
  const canvasRef = useRef<DrawCanvasHandle>(null);
  const restoredFor = useRef<string | null>(null);

  const [, forceRender] = useState(0);
  const [submitState, setSubmitState] = useState<SubmitState>({ phase: 'idle' });
  const [confirming, setConfirming] = useState(false);

  /* ------------------------------------------------------------- layout */

  const [layout, setLayoutState] = useState<DrawLayout>(initialLayout);
  const [peeking, setPeeking] = useState(false);

  const setLayout = (next: DrawLayout) => {
    setLayoutState(next);
    try {
      localStorage.setItem(LAYOUT_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const assignment: Assignment | undefined = state?.assignments.find((a) => a.panelId === panelId);
  const submitting = submitState.phase === 'sending' || submitState.phase === 'retrying';

  /* ---------------------------------------------------------- activity */

  useEffect(() => {
    trackPanel(panelId ?? null);
    return () => trackPanel(null);
  }, [panelId]);

  /** A drawing action: stroke, undo, redo, straighten. Counted, never inspected. */
  const acted = () => {
    if (panelId) noteAction(panelId);
  };

  /* ------------------------------------------------------------- tools */

  const [tool, setToolState] = useState<ToolId>('pen');
  const setTool = (next: ToolId) => {
    setToolState(next);
    session.setPen(TOOLS.find((t) => t.id === next)!.pen);
  };

  const [hint, setHint] = useState<string | null>(null);
  const showHint = (message: string) => {
    setHint(message);
    window.setTimeout(() => setHint((current) => (current === message ? null : current)), SNAP_HINT_MS);
  };

  /* -------------------------------------------------------------- size */

  // Round 2.2 §3: re-render when the panel crosses into "very detailed" or "full"
  // mid-stroke, without re-rendering on every point.
  const sizeLevelRef = useRef<SizeLevel>(session.sizeLevel);
  // Whatever this render shows is the baseline (undo and redo re-render too).
  sizeLevelRef.current = session.sizeLevel;
  const checkSize = () => {
    if (session.sizeLevel !== sizeLevelRef.current) {
      sizeLevelRef.current = session.sizeLevel;
      forceRender((n) => n + 1);
    }
  };
  const onBlocked = () => showHint('This panel is full. Undo to make room, or send it.');

  const onSnap = () => {
    acted();
    try {
      if (localStorage.getItem(SNAP_HINT_KEY)) return;
      localStorage.setItem(SNAP_HINT_KEY, '1');
    } catch {
      return;
    }
    showHint('Straightened — tap undo to revert.');
  };

  /* ------------------------------------------------------------ drafts */

  // GUARDRAIL #8: a refresh, a back-swipe or a browser kill restores the drawing
  // exactly, undo stack included.
  useEffect(() => {
    if (!panelId || restoredFor.current === panelId) return;
    restoredFor.current = panelId;
    const draft = loadDraft(panelId);
    if (draft) {
      session.restore({ ops: draft.ops, redo: draft.redo });
      canvasRef.current?.repaint();
      forceRender((n) => n + 1);
    }
  }, [panelId, session]);

  const lastSave = useRef(0);
  // Once the server has the panel, the draft is gone for good: any later save —
  // including the one on unmount as we navigate away — would resurrect it.
  const submitted = useRef(false);

  const persist = (force = false) => {
    if (!panelId || submitted.current) return;
    const now = Date.now();
    if (!force && now - lastSave.current < DRAFT_THROTTLE_MS) return;
    lastSave.current = now;
    saveDraft(panelId, session.snapshot());
  };

  // Anything that takes the page away is a save point.
  const persistRef = useRef(persist);
  persistRef.current = persist;

  useEffect(() => {
    const save = () => persistRef.current(true);
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', save);
    return () => {
      window.removeEventListener('pagehide', save);
      document.removeEventListener('visibilitychange', save);
      save();
    };
  }, []);

  /* -------------------------------------------------------- undo / redo */

  const holdTimer = useRef<number | null>(null);
  const rewindFrame = useRef<number | null>(null);
  const didHold = useRef(false);

  const stopRewind = () => {
    if (rewindFrame.current !== null) cancelAnimationFrame(rewindFrame.current);
    rewindFrame.current = null;
    if (holdTimer.current !== null) clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };

  const startHold = () => {
    didHold.current = false;
    holdTimer.current = window.setTimeout(() => {
      didHold.current = true;
      if (!session.beginPartialUndo()) return;
      // A straightened stroke has just gone back to hand-drawn; show that at once.
      canvasRef.current?.repaint();

      const last = session.ops[session.ops.length - 1];
      const length = last && last.kind === 'stroke' ? last.points.length : 0;

      const startedAt = performance.now();
      let removed = 0;

      const step = (now: number) => {
        // Driven by elapsed time, not frames rendered (see pointsRewoundAfter).
        const due = pointsRewoundAfter(length, now - startedAt);
        let more = true;
        if (due > removed) {
          more = session.stepPartialUndo(due - removed);
          removed = due;
          canvasRef.current?.repaint();
          forceRender((n) => n + 1);
        }
        rewindFrame.current = more ? requestAnimationFrame(step) : null;
      };
      rewindFrame.current = requestAnimationFrame(step);
    }, HOLD_THRESHOLD_MS);
  };

  const endHold = () => {
    stopRewind();
    if (didHold.current) {
      // Release stops the rewind exactly where it got to.
      session.endPartialUndo();
    } else {
      // A tap takes the whole op (or, on a stroke snapped mid-way, just the snap).
      session.undo();
    }
    acted();
    didHold.current = false;
    canvasRef.current?.repaint();
    forceRender((n) => n + 1);
    persist(true);
  };

  const cancelHold = () => {
    stopRewind();
    if (didHold.current) {
      session.endPartialUndo();
      acted();
    }
    didHold.current = false;
    canvasRef.current?.repaint();
    forceRender((n) => n + 1);
    persist(true);
  };

  const redo = () => {
    session.redoStep();
    acted();
    canvasRef.current?.repaint();
    forceRender((n) => n + 1);
    persist(true);
  };

  useEffect(() => stopRewind, []);

  /* ---------------------------------------------------------- focus fit */

  const stageRef = useRef<HTMLDivElement>(null);
  const [fitWidth, setFitWidth] = useState<number | null>(null);
  const ratio = assignment ? aspectValue(assignment.aspectRatio) : null;

  // "As much screen as possible" without cropping: the canvas is bounded by the
  // stage's width AND its height, whichever bites first for this aspect ratio.
  useEffect(() => {
    if (layout !== 'focus' || ratio === null) {
      setFitWidth(null);
      return;
    }
    const node = stageRef.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      setFitWidth(Math.max(120, Math.min(rect.width, rect.height * ratio)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [layout, ratio]);

  /* ------------------------------------------------------------- submit */

  const submit = async () => {
    if (!panelId) return;
    setConfirming(false);
    // Written to the local outbox before the network is touched (guardrail #7).
    const log = session.toOpLog();
    // The drawing screen never lets a panel grow past the limit, but a submit must
    // not be able to exceed it whatever happened (an old draft, a bug): check here
    // too, so the answer is a message rather than a panel the server refuses.
    if (opLogBytes(log) > MAX_OPLOG_BYTES) {
      setSubmitState({ phase: 'failed', error: 'This panel is too detailed to send. Undo a few strokes, then try again.' });
      return;
    }
    queueSubmission(panelId, log);
    const ok = await flushSubmission(panelId, setSubmitState);
    if (ok) {
      submitted.current = true;
      clearDraft(panelId);
      clearActivity(panelId);
      navigate(`/room/${roomId}`, { replace: true });
    }
  };

  /* -------------------------------------------------------------- render */

  if (loading && !state) return <Spinner label="Loading your panel…" />;

  if (!assignment) {
    // Submitted, reassigned after a skip, or the round moved on without us.
    return (
      <Screen title="Panel" back={`/room/${roomId}`}>
        <p className="muted center">
          {submitState.phase === 'confirmed' ? 'Panel sent.' : 'This panel is not yours to draw right now.'}
        </p>
        <button type="button" className="button primary" onClick={() => navigate(`/room/${roomId}`, { replace: true })}>
          Back to the room
        </button>
      </Screen>
    );
  }

  const sideBySide = aspectValue(assignment.aspectRatio) <= 0.6;
  const isFirst = assignment.position === 1;
  const isLast = !isFirst && assignment.position === assignment.panelCount;
  const focus = layout === 'focus';

  const drawCanvas = (
    <DrawCanvas
      ref={canvasRef}
      session={session}
      aspectRatio={assignment.aspectRatio}
      onChange={() => {
        acted();
        persist(true);
        forceRender((n) => n + 1);
      }}
      onDrawing={() => {
        persist(false);
        checkSize();
      }}
      onBlocked={onBlocked}
      onSnap={onSnap}
      onInput={() => panelId && noteInput(panelId)}
      disabled={submitting || peeking}
    />
  );

  return (
    <div className="draw-screen">
      <header className="draw-header">
        {/* Which panel this is must be unmistakable (Round 2 §2.1). */}
        <span className={`draw-position ${isFirst ? 'is-first' : isLast ? 'is-last' : ''}`}>
          {isFirst
            ? 'You are starting a new comic'
            : isLast
              ? 'Final panel'
              : `Panel ${assignment.position} of ${assignment.panelCount}`}
        </span>
        <span className="draw-hint">
          {isFirst ? '' : `Riff on ${assignment.previousAuthor?.displayName ?? 'this'}`}
        </span>
        {/* Panel controls live together up here: peek at the previous panel,
            switch between the big canvas and the split view. */}
        {focus && !isFirst ? (
          <button
            type="button"
            className="peek-button"
            aria-label="Look at the previous panel"
            onClick={() => setPeeking(true)}
          >
            <span
              className="peek-thumb"
              style={{ width: Math.round(26 * aspectValue(assignment.aspectRatio)) }}
            >
              <PanelCanvas ops={assignment.previousOps} aspectRatio={assignment.aspectRatio} />
            </span>
            Peek
          </button>
        ) : null}
        <button type="button" className="layout-toggle" onClick={() => setLayout(focus ? 'split' : 'focus')}>
          {focus ? 'Split view' : 'Big canvas'}
        </button>
      </header>

      {focus ? (
        /* Focus: the canvas gets everything the screen has. The previous panel is
           still one glance away — a live thumbnail on the Peek button above. */
        <div className="draw-stage focus" ref={stageRef}>
          <div className="draw-active" style={fitWidth ? { width: fitWidth } : undefined}>{drawCanvas}</div>
        </div>
      ) : (
        /* Split: the brief's shared view — previous panel beside/above the canvas. */
        <div className={`draw-stage ${sideBySide ? 'side-by-side' : 'stacked'}`}>
          <div className="draw-previous">
            {isFirst ? (
              <div className="blank-note" style={{ aspectRatio: assignment.aspectRatio.replace(':', ' / ') }}>
                <span>Blank start</span>
              </div>
            ) : (
              <PanelCanvas
                ops={assignment.previousOps}
                aspectRatio={assignment.aspectRatio}
                placeholder="Nothing to see"
              />
            )}
            <span className="draw-caption">{isFirst ? 'No panel before yours' : 'Previous panel'}</span>
          </div>

          <div className="draw-active">
            {drawCanvas}
            <span className="draw-caption">Your panel</span>
          </div>
        </div>
      )}

      {peeking ? (
        <div className="peek-overlay" onClick={() => setPeeking(false)}>
          <div
            className="peek-panel"
            style={{ width: `min(86vw, ${Math.round(68 * aspectValue(assignment.aspectRatio))}dvh)` }}
          >
            <PanelCanvas ops={assignment.previousOps} aspectRatio={assignment.aspectRatio} />
            <span className="draw-caption">
              {assignment.previousAuthor
                ? `${assignment.previousAuthor.displayName} drew this — tap to go back`
                : 'Previous panel — tap to go back'}
            </span>
          </div>
        </div>
      ) : null}

      {hint ? (
        <div className="draw-toast" role="status">
          {hint}
        </div>
      ) : null}

      <div className="draw-tools">
        <ToolPicker value={tool} onChange={setTool} disabled={submitting} />

        <button
          type="button"
          className="tool"
          disabled={!session.canUndo}
          onPointerDown={startHold}
          onPointerUp={endHold}
          onPointerLeave={cancelHold}
          onPointerCancel={cancelHold}
          onContextMenu={(e) => e.preventDefault()}
        >
          <span className="tool-glyph">↶</span>
          <span className="tool-label">Undo</span>
        </button>

        <button type="button" className="tool" disabled={!session.canRedo} onClick={redo}>
          <span className="tool-glyph">↷</span>
          <span className="tool-label">Redo</span>
        </button>

        <button
          type="button"
          className="button primary done"
          onClick={() => setConfirming(true)}
          disabled={submitting || session.isEmpty}
        >
          Done
        </button>
      </div>

      {session.sizeLevel === 'full' ? (
        <div className="banner banner-warn size-note" role="status">
          This panel is full: it's as detailed as a panel can be. Undo to make room, or send it.
        </div>
      ) : session.sizeLevel === 'warn' ? (
        <p className="size-note muted small" role="status">
          This panel is getting very detailed.
        </p>
      ) : null}

      <p className="draw-tip muted small">
        Hold undo to rewind a stroke bit by bit. Hold Shift for a straight line.
      </p>

      {submitState.phase !== 'idle' ? <SubmitBanner state={submitState} onRetry={submit} /> : null}

      {confirming ? (
        <div className="sheet-backdrop" onClick={() => setConfirming(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h2>{isLast ? 'Send the final panel?' : 'Send this panel?'}</h2>
            <p className="muted">
              {isLast
                ? 'This finishes the comic. You cannot change it after this.'
                : 'You cannot change it after this.'}
            </p>
            <button type="button" className="button primary" onClick={submit}>
              Send it
            </button>
            <button type="button" className="button" onClick={() => setConfirming(false)}>
              Keep drawing
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Submission state, told honestly (§7). */
function SubmitBanner({ state, onRetry }: { state: SubmitState; onRetry: () => void }) {
  if (state.phase === 'confirmed') return <div className="banner banner-ok">Panel sent</div>;
  if (state.phase === 'sending') return <div className="banner">Sending your panel…</div>;
  if (state.phase === 'retrying') {
    return (
      <div className="banner banner-warn">
        Could not reach the server — trying again (attempt {state.attempt}). Your drawing is safe.
      </div>
    );
  }
  if (state.phase === 'failed') {
    return (
      <div className="banner banner-error">
        <span>{state.error} Your drawing is saved on this device.</span>
        <button type="button" className="link-button" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }
  return null;
}
